import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ResourceDialogForm from './ResourceDialogForm';
import { useEnvironment } from './EnvironmentProvider';
import { useDesignSystem, cn } from './designSystem';

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Plus, Edit, Trash, Link2, FileText, Video, File, Music, Copy } from "lucide-react";

const ResourceBank = () => {
  const { colors, typography, effects, componentStyles, shadows } = useDesignSystem();
  const [resources, setResources] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingResource, setEditingResource] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Endret: Sett default språk til "no" for nye ressurser
  const emptyResource = {
    title: '',
    url: '',
    description: '',
    type: 'article', 
    language: 'no',
    tags: []
  };
  
  const { API_BASE_URL } = useEnvironment();

  // Sjekk om vi er på mobil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchResources();
  }, []);

  // Standardiser ressursene for å sikre at språk alltid er definert
  const standardizeResource = (resource) => {
    return {
      ...resource,
      language: resource.language || 'no',
      type: resource.type || 'video',
      tags: Array.isArray(resource.tags) ? resource.tags : []
    };
  };

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/resources`);
      // Standardiser alle ressurser for å sikre konsistente verdier
      const standardizedResources = response.data.map(standardizeResource);
      console.log('Resources fetched and standardized:', standardizedResources);
      setResources(standardizedResources);
    } catch (error) {
      console.error('Error fetching resources:', error.response || error);
    }
  };

  const handleAddResource = async (formData) => {
    setIsSaving(true);
    try {
      // Standardiser språkverdien (til små bokstaver)
      const resourceData = {
        ...formData,
        language: (formData.language || 'no').toLowerCase(),
        type: formData.type || 'video'
      };
      
      console.log('Sending resource data:', resourceData);
      
      const response = await axios.post(`${API_BASE_URL}/resources`, resourceData);
      console.log('API response after adding:', response.data);
      
      setShowAddForm(false);
      
      // Legg til liten forsinkelse før henting av ressurser
      // for å sikre at serveren har oppdatert dataene
      setTimeout(() => {
        fetchResources();
      }, 300);
    } catch (error) {
      console.error('Error adding resource:', error);
      alert('Det oppstod en feil ved lagring av ressursen. Vennligst prøv igjen.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleUpdateResource = async (formData) => {
    setIsSaving(true);
    try {
      const resourceData = {
        ...formData,
        id: editingResource.id,
        language: (formData.language || 'no').toLowerCase(),
        type: formData.type || 'video'
      };
      
      console.log('Updating resource data:', resourceData);
      
      const response = await axios.put(`${API_BASE_URL}/resources/${editingResource.id}`, resourceData);
      console.log('API response after updating:', response.data);
      
      setEditingResource(null);
      
      // Legg til liten forsinkelse før henting av ressurser
      setTimeout(() => {
        fetchResources();
      }, 300);
    } catch (error) {
      console.error('Error updating resource:', error);
      alert('Det oppstod en feil ved oppdatering av ressursen. Vennligst prøv igjen.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeleteResource = async (id) => {
    if (!window.confirm('Er du sikker på at du vil slette denne ressursen?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/resources/${id}`);
      fetchResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Det oppstod en feil ved sletting av ressursen.');
    }
  };
  
  const filteredResources = resources.filter(resource => 
    resource.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (resource.tags?.length ? resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) : false)
  );

  // Bruk lowercase for konsistent badge-visning
  const getLanguageBadgeClass = (language) => {
    const lang = (language || '').toLowerCase();
    switch (lang) {
      case 'no':
        return colors.language.no;
      case 'en':
        return colors.language.en;
      default:
        return colors.language.other;
    }
  };

  // Egen funksjon for tagfarger (ikke bruk getLanguageBadgeClass for tags)
  const getTagColor = (tag) => {
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = tag.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorOptions = [
      cn(colors.primary.light, colors.primary.text),
      cn(colors.success.light, colors.success.text),
      cn(colors.warning.light, colors.warning.text),
      "bg-purple-50 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300",
      "bg-pink-50 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300",
      "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300",
      "bg-teal-50 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300",
      "bg-lime-50 text-lime-700 dark:bg-lime-900/50 dark:text-lime-300",
      "bg-sky-50 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300",
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
      "bg-amber-50 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
      "bg-slate-50 text-slate-700 dark:bg-slate-900/50 dark:text-slate-300",
      "bg-rose-50 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300",
      "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300",
      "bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-900/50 dark:text-fuchsia-300",
      "bg-orange-50 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300",
      "bg-blue-400/20 text-blue-800 dark:bg-blue-800/40 dark:text-blue-200",
    ];
    
    return colorOptions[Math.abs(hash) % colorOptions.length];
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'video':
        return <Video className={cn(componentStyles.button.iconSizes.sm)} />;
      case 'article':
        return <FileText className={cn(componentStyles.button.iconSizes.sm)} />;
      case 'pdf':
        return <File className={cn(componentStyles.button.iconSizes.sm)} />;
      case 'lydfil':
        return <Music className={cn(componentStyles.button.iconSizes.sm)} />;
      default:
        return <Link2 className={cn(componentStyles.button.iconSizes.sm)} />;
    }
  };

  // Conditionally render for mobile or desktop
  if (isMobile) {
    return (
      <div className="h-full flex flex-col max-w-full overflow-hidden">
        {/* Søke- og Add-knapper */}
        <div className="p-3 pb-2 flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <Input
              type="text"
              placeholder="Søk i ressurser..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                "flex-1",
                colors.neutral.bg,
                "dark:bg-neutral-800 dark:text-neutral-100 dark:border-neutral-700",
                "text-sm shadow-sm"
              )}
            />
            <Button 
              onClick={() => setShowAddForm(true)}
              size="sm"
              className={cn(
                componentStyles.button.base,
                componentStyles.button.primary,
                "shrink-0 p-2 shadow-sm"
              )}
            >
              <Plus className={cn(componentStyles.button.iconSizes.sm)} />
            </Button>
          </div>
        </div>
        
        {/* Dialogskjemaer */}
        <ResourceDialogForm 
          isOpen={showAddForm}
          onClose={() => setShowAddForm(false)}
          initialResource={emptyResource}
          onSubmit={handleAddResource}
          isSaving={isSaving}
        />
        
        <ResourceDialogForm 
          isOpen={!!editingResource}
          onClose={() => setEditingResource(null)}
          initialResource={editingResource}
          onSubmit={handleUpdateResource}
          isEditing={true}
          isSaving={isSaving}
        />
  
        {/* Ressursliste - Optimalisert for mobil */}
        <div className="overflow-y-auto h-[calc(100vh-70px)] flex-grow">
          <div className="space-y-3 p-3 pb-6 w-full">
            {filteredResources.map((resource) => (
              <div 
                key={resource.id} 
                className={cn(
                  colors.neutral.bg,
                  "dark:bg-neutral-800 border",
                  colors.neutral.border,
                  "rounded-md p-3 shadow-sm hover:shadow-md",
                  effects.transition,
                  "w-full max-w-full"
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 max-w-[65%]">
                    <span className={cn(colors.primary.text, "flex-shrink-0")}>
                      {getResourceIcon(resource.type)}
                    </span>
                    <h3 className={cn(
                      typography.cardTitle,
                      "truncate text-sm"
                    )}>
                      {resource.title}
                    </h3>
                  </div>
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    <span className={cn(
                      "text-xs px-1.5 py-0.5 rounded",
                      getLanguageBadgeClass(resource.language)
                    )}>
                      {resource.language?.toLowerCase() === 'no'
                        ? 'NO'
                        : resource.language?.toLowerCase() === 'en'
                        ? 'EN'
                        : '?'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingResource(resource)}
                      className={cn(
                        "h-6 w-6 p-0 flex items-center justify-center",
                        colors.neutral.textTertiary,
                        "hover:text-blue-500 dark:hover:text-blue-400"
                      )}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteResource(resource.id)}
                      className={cn(
                        "h-6 w-6 p-0 flex items-center justify-center",
                        colors.neutral.textTertiary,
                        "hover:text-red-500 dark:hover:text-red-400"
                      )}
                    >
                      <Trash className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                
                <p className={cn(
                  typography.small,
                  colors.neutral.textSecondary,
                  "mb-2",
                  effects.lineClamp2
                )}>
                  {resource.description}
                </p>
                
                {/* Tags */}
                {resource.tags && resource.tags.length > 0 && (
                  <ScrollArea className="w-full" orientation="horizontal">
                    <div className="flex gap-1 mb-2">
                      {resource.tags.map(tag => (
                        <span 
                          key={tag} 
                          className={cn(
                            getTagColor(tag),
                            componentStyles.badge.base,
                            "whitespace-nowrap"
                          )}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </ScrollArea>
                )}
                
                {/* Action buttons */}
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(resource.url);
                      const button = document.getElementById(`copy-${resource.id}`);
                      if (button) {
                        const originalText = button.innerText;
                        button.innerText = 'Kopiert!';
                        setTimeout(() => {
                          button.innerText = originalText;
                        }, 2000);
                      }
                    }}
                    id={`copy-${resource.id}`}
                    className={cn(
                      colors.neutral.textSecondary,
                      "text-xs flex items-center gap-1",
                      colors.neutral.bgSecondary,
                      "hover:bg-blue-50 px-2 py-1 rounded flex-1 justify-center dark:hover:bg-neutral-700",
                      effects.transition
                    )}
                  >
                    <Copy className="h-3 w-3" />
                    Kopier
                  </button>
                  <a 
                    href={resource.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={cn(
                      colors.primary.text,
                      "text-xs flex items-center gap-1 px-2 py-1 rounded",
                      colors.primary.light,
                      "flex-1 justify-center",
                      effects.transition
                    )}
                  >
                    <Link2 className="h-3 w-3" />
                    Åpne
                  </a>
                </div>
              </div>
            ))}
            
            {filteredResources.length === 0 && (
              <div className={cn(
                colors.neutral.textTertiary,
                typography.center,
                "py-8"
              )}>
                {searchTerm ? 'Ingen ressurser matcher søket' : 'Ingen ressurser er lagt til ennå'}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop-versjon
  return (
    <div className="h-full flex flex-col p-6">
      <div className="mb-6 flex-shrink-0">
        <div className="flex items-center justify-end space-x-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Søk i ressurser..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                "w-full",
                colors.neutral.bg,
                "dark:bg-neutral-900 dark:text-neutral-100 dark:border-neutral-700",
                shadows.sm
              )}
            />
          </div>
          <Button 
            onClick={() => setShowAddForm(true)}
            className={cn(
              componentStyles.button.base,
              componentStyles.button.primary,
              shadows.sm
            )}
          >
            <Plus className="mr-2 h-4 w-4" />
            Legg til ressurs
          </Button>
        </div>
      </div>
      
      {/* Dialogskjemaer */}
      <ResourceDialogForm 
        isOpen={showAddForm}
        onClose={() => setShowAddForm(false)}
        initialResource={emptyResource}
        onSubmit={handleAddResource}
        isSaving={isSaving}
      />
      
      <ResourceDialogForm 
        isOpen={!!editingResource}
        onClose={() => setEditingResource(null)}
        initialResource={editingResource}
        onSubmit={handleUpdateResource}
        isEditing={true}
        isSaving={isSaving}
      />
      
      <ScrollArea className="flex-grow">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-6">
          {filteredResources.map((resource) => (
            <Card 
              key={resource.id} 
              className={cn(
                componentStyles.card.base,
                componentStyles.card.hover,
                "h-[300px]",
                shadows.sm
              )}
            >
              <div className="h-full flex flex-col">
                <CardHeader className="pb-2 flex-shrink-0">
                  <CardTitle className="flex justify-between items-center text-lg">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="flex items-center gap-2 max-w-[calc(100%-80px)]">
                          <span className={colors.primary.text}>{getResourceIcon(resource.type)}</span>
                          <span className={cn(colors.neutral.textPrimary, effects.truncate)}>
                            {resource.title}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{resource.title}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <div className="flex items-center space-x-1">
                      <span className={cn(
                        "text-xs px-1.5 py-0.5 rounded",
                        getLanguageBadgeClass(resource.language)
                      )}>
                        {resource.language?.toLowerCase() === 'no'
                          ? 'NO'
                          : resource.language?.toLowerCase() === 'en'
                          ? 'EN'
                          : '?'}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingResource(resource)}
                        className={cn(
                          "hover:text-blue-500",
                          colors.neutral.textSecondary,
                          "dark:hover:text-blue-400 px-1.5"
                        )}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteResource(resource.id)}
                        className={cn(
                          "hover:text-red-500",
                          colors.neutral.textSecondary,
                          "dark:hover:text-red-400 px-1.5"
                        )}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow flex flex-col overflow-hidden p-4">
                  <ScrollArea className="flex-grow -mr-4 pr-4">
                    <p className={cn(typography.body, colors.neutral.textSecondary)}>
                      {resource.description}
                    </p>
                  </ScrollArea>
                  <div className="mt-4 flex-shrink-0">
                    <ScrollArea className="w-full" orientation="horizontal">
                      <div className="flex gap-1 pb-2">
                        {resource.tags && resource.tags.map(tag => (
                          <span 
                            key={tag} 
                            className={cn(
                              getTagColor(tag), // FIKSET: Bruker getTagColor for tags
                              componentStyles.badge.base,
                              "whitespace-nowrap"
                            )}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </ScrollArea>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(resource.url);
                          const button = document.getElementById(`copy-${resource.id}`);
                          if (button) {
                            const originalText = button.innerText;
                            button.innerText = 'Kopiert!';
                            setTimeout(() => {
                              button.innerText = originalText;
                            }, 2000);
                          }
                        }}
                        id={`copy-${resource.id}`}
                        className={cn(
                          colors.neutral.textSecondary,
                          "hover:text-blue-600 text-sm flex items-center gap-1",
                          colors.neutral.bgSecondary,
                          "hover:bg-blue-50 px-2 py-1 rounded-md transition-colors",
                          "dark:hover:bg-neutral-700 dark:hover:text-blue-400"
                        )}
                      >
                        <Copy className="h-3 w-3" />
                        Kopier lenke
                      </button>
                      <a 
                        href={resource.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={cn(
                          colors.primary.text,
                          "hover:bg-blue-50 text-sm flex items-center gap-1 px-2 py-1 rounded-md transition-colors",
                          "dark:hover:bg-neutral-700",
                          colors.primary.light,
                          "dark:bg-blue-900/20"
                        )}
                      >
                        <Link2 className="h-3 w-3" />
                        Åpne ressurs
                      </a>
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
        {filteredResources.length === 0 && (
          <div className={cn(
            colors.neutral.textTertiary,
            typography.center,
            "py-8"
          )}>
            {searchTerm ? 'Ingen ressurser matcher søket' : 'Ingen ressurser er lagt til ennå'}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default ResourceBank;